import { Link, Navigate, useParams } from "react-router-dom";
import { FiEdit2 } from "react-icons/fi";
import Badge from "components/Badge";
import Card from "components/Card";
import AppCarousel from "components/common/AppCarousel";
import AppBreadcrumbs from "components/common/AppBreadcrumbs";
import ListFetchErrorBanner from "components/common/ListFetchErrorBanner";
import FundingSnapshotCard from "pages/project/details/FundingSnapshotCard";
import ProjectDetailsTabsPanel from "pages/project/details/ProjectDetailsTabsPanel";
import ProjectOverviewTab from "pages/project/details/tabs/ProjectOverviewTab";
import { ProjectDetailsProvider, useProjectDetailsContext } from "pages/project/context/ProjectDetailsContext";

function ProjectDetailsContent() {
  const { project, loading, loadError, missing, reload } = useProjectDetailsContext();

  if (missing && !loading) {
    return <Navigate to="/projects" replace />;
  }

  if (loadError) {
    return (
      <div className="space-y-6">
        <AppBreadcrumbs
          items={[
            { label: "Projects", path: "/projects" },
            { label: "Project", path: "#" },
          ]}
        />
        <Card>
          <ListFetchErrorBanner
            message={loadError}
            onRetry={reload}
            loading={loading}
            retryAriaLabel="Reload project"
          />
          <Link
            to="/projects"
            className="inline-flex rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-text-700 transition hover:bg-slate-50"
          >
            Back to projects
          </Link>
        </Card>
      </div>
    );
  }

  if (loading && !project) {
    return (
      <div className="space-y-6">
        <AppBreadcrumbs
          items={[
            { label: "Projects", path: "/projects" },
            { label: "Loading…", path: "#" },
          ]}
        />
        <Card>
          <p className="text-sm text-text-600">Loading project…</p>
        </Card>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="space-y-6">
        <AppBreadcrumbs
          items={[
            { label: "Projects", path: "/projects" },
            { label: "Project", path: "#" },
          ]}
        />
        <Card>
          <p className="mb-4 text-sm text-red-800" role="alert">
            Project could not be loaded.
          </p>
          <Link
            to="/projects"
            className="inline-flex rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-text-700 transition hover:bg-slate-50"
          >
            Back to projects
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <AppBreadcrumbs
        items={[
          { label: "Projects", path: "/projects" },
          { label: project.title, path: `/projects/${project.id}` },
        ]}
      />

      <Card
        className="my-3 border-slate-200"
        title="Project Details"
        action={
          <div className="flex flex-wrap items-center gap-2">
            <Badge status={project.status.toLowerCase()} className="text-capitalize" />
            {project.isSample ? (
              <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800">
                Sample Project
              </span>
            ) : null}
            <Link
              to={`/projects/${project.id}/edit`}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-900 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-brand-800"
            >
              <FiEdit2 className="h-3.5 w-3.5" aria-hidden />
              Edit
            </Link>
            <Link
              to="/projects"
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-text-700 transition hover:bg-slate-50"
            >
              Back to list
            </Link>
          </div>
        }
      />
      <Card>
        <div className="space-y-6">
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12">
              <AppCarousel
                items={project.images?.length ? project.images : ["/images/fortify_logo_black.svg"]}
                autoPlay
                autoPlayInterval={3500}
                heightClassName="h-[280px] md:h-[420px]"
              />
            </div>
          </div>
          <Card className="my-3 border-slate-200">
            <ProjectOverviewTab />
          </Card>
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 lg:col-span-9">
              <ProjectDetailsTabsPanel />
            </div>
            <div className="col-span-12 lg:col-span-3">
              <FundingSnapshotCard />
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default function ProjectDetailsPage() {
  const { projectId } = useParams();

  return (
    <ProjectDetailsProvider projectId={projectId}>
      <ProjectDetailsContent />
    </ProjectDetailsProvider>
  );
}
